"use client";

import { BBButton } from "@/lib";
import { Box, Card, Typography } from "@mui/material";
import { TriangleAlert } from "lucide-react";
import { useSelectedLayoutSegment } from "next/navigation";

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  // Safer than usePathname in global error
  const segment = useSelectedLayoutSegment();

  // Client-side logging only
  if (typeof window !== "undefined") {
    console.error("Global Error Boundary", {
      message: error?.message,
      digest: error?.digest,
      stack: error?.stack,
      segment: segment ?? "root",
      timestamp: new Date().toISOString(),
    });
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        bgcolor: "grey.100",
      }}
    >
      {/* LEFT SIDE – USER FRIENDLY */}
      <Box
        sx={{
          flex: 1,
          p: { xs: 3, sm: 4 },
          bgcolor: "white",
          borderRight: { xs: "none", md: "1px solid #e0e0e0" },
          borderBottom: { xs: "1px solid #e0e0e0", md: "none" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Box sx={{ color: "error.main", mb: 2 }}>
          <TriangleAlert size={48} />
        </Box>

        <Typography variant="h5" fontWeight={600} mb={1}>
          Something went wrong
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: "warning.main",
            mb: 2,
            fontSize: "0.75rem",
            wordBreak: "break-word",
          }}
        >
          Route segment: {segment ?? "root"}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 3,
            maxWidth: 360,
            fontSize: "0.95rem",
          }}
        >
          {error?.message || "An unexpected error occurred. Please try again."}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <BBButton variant="contained" fullWidth onClick={reset}>
            Try Again
          </BBButton>

          <BBButton variant="outlined" color="inherit" fullWidth onClick={() => window.location.reload()}>
            Reload Page
          </BBButton>
        </Box>

        {process.env.NODE_ENV === "development" && (
          <Typography variant="caption" sx={{ color: "text.disabled", mt: 3, fontSize: "0.7rem" }}>
            Open DevTools (F12) to see error logs
          </Typography>
        )}
      </Box>

      {/* RIGHT SIDE – DEV DETAILS (DESKTOP) */}
      {/* {process.env.NODE_ENV === "development" && ( */}
      <Box
        sx={{
          flex: 1,
          p: 3,
          bgcolor: "grey.900",
          color: "success.light",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} color="white" mb={2}>
          Debug Information
        </Typography>

        <Card
          sx={{
            bgcolor: "grey.950",
            p: 2,
            mb: 2,
            border: "1px solid #e53935",
          }}
        >
          <Typography variant="caption" color="#ef5350">
            Message
          </Typography>
          <Box component="pre" sx={preStyle}>
            {error?.message || "No message"}
          </Box>
        </Card>

        <Card
          sx={{
            bgcolor: "grey.950",
            p: 2,
            border: "1px solid #1565c0",
            flex: 1,
          }}
        >
          <Typography variant="caption" color="#42a5f5">
            Stack Trace
          </Typography>
          <Box component="pre" sx={preStyle}>
            {error?.stack || "No stack trace"}
          </Box>
        </Card>

        {error?.digest && (
          <Typography variant="caption" sx={{ mt: 2, color: "warning.light" }}>
            Error digest: {error.digest}
          </Typography>
        )}
      </Box>
      {/* )} */}
    </Box>
  );
}

const preStyle = {
  fontSize: "0.8rem",
  mt: 1,
  whiteSpace: "pre-wrap",
  overflowX: "auto",
};
